import { Folder, Download, Trash2, MoreVertical, Check, Pencil } from "lucide-react";
import { fileIcon, fmtSize } from "./driveUtils";

export default function DriveGridView({
  items, selected, renaming, renameValue, setRenameValue,
  menuOpen, setMenuOpen, onOpen, onToggleSelect, onDownload, onDelete, onStartRename, onSubmitRename
}) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
      {items.map(item => (
        <GridCard
          key={item.path}
          item={item}
          selected={selected}
          renaming={renaming}
          renameValue={renameValue}
          setRenameValue={setRenameValue}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          onOpen={onOpen}
          onToggleSelect={onToggleSelect}
          onDownload={onDownload}
          onDelete={onDelete}
          onStartRename={onStartRename}
          onSubmitRename={onSubmitRename}
        />
      ))}
    </div>
  );
}

function GridCard({ item, selected, renaming, renameValue, setRenameValue, menuOpen, setMenuOpen,
  onOpen, onToggleSelect, onDownload, onDelete, onStartRename, onSubmitRename }) {
  const isSelected = selected.has(item.path);
  const isMenuOpen = menuOpen === item.path;
  const isRenaming = renaming === item.path;

  return (
    <div
      onDoubleClick={() => onOpen(item)}
      className={`relative group p-3 border cursor-pointer transition-all hover:shadow-sm ${
        isSelected ? "border-[#A8A67A] bg-[#F5F4EE]" : "border-border bg-white hover:border-[#D4D2B0]"
      }`}
      data-testid={`item-${item.name}`}
    >
      <div
        onClick={(e) => onToggleSelect(e, item.path)}
        className={`absolute top-2 left-2 w-4 h-4 border flex items-center justify-center transition-opacity cursor-pointer ${
          isSelected ? "opacity-100 border-[#A8A67A] bg-[#A8A67A]" : "opacity-0 group-hover:opacity-100 border-border bg-white"
        }`}
      >
        {isSelected && <Check className="w-2.5 h-2.5 text-white" />}
      </div>

      <div className="absolute top-1.5 right-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => { e.stopPropagation(); setMenuOpen(isMenuOpen ? null : item.path); }}
          className="p-1 text-muted-foreground hover:text-foreground hover:bg-[#F5F4EE]"
        >
          <MoreVertical className="w-3.5 h-3.5" />
        </button>
        {isMenuOpen && (
          <div className="absolute right-0 top-6 z-20 bg-white border border-border shadow-lg w-36 py-1" onClick={e => e.stopPropagation()}>
            {item.type === "file" && (
              <button onClick={() => { onDownload(item.path); setMenuOpen(null); }}
                className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-[#F5F4EE] text-left">
                <Download className="w-3.5 h-3.5 text-[#A8A67A]" /> Download
              </button>
            )}
            <button onClick={() => onStartRename(item)}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-[#F5F4EE] text-left">
              <Pencil className="w-3.5 h-3.5 text-[#A8A67A]" /> Rename
            </button>
            <button onClick={() => { onDelete(item.path); setMenuOpen(null); }}
              className="flex items-center gap-2 w-full px-3 py-1.5 text-xs hover:bg-red-50 text-red-600 text-left">
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        )}
      </div>

      <div className="flex justify-center mt-2 mb-3">
        {item.type === "folder"
          ? <div className="w-10 h-10 bg-amber-50 flex items-center justify-center"><Folder className="w-6 h-6 text-amber-500" /></div>
          : fileIcon(item)}
      </div>

      {isRenaming ? (
        <input
          autoFocus
          value={renameValue}
          onChange={e => setRenameValue(e.target.value)}
          onBlur={() => onSubmitRename(item)}
          onKeyDown={e => { if (e.key === "Enter") onSubmitRename(item); if (e.key === "Escape") onSubmitRename(item); }}
          className="w-full text-xs text-center border border-[#A8A67A] px-1 py-0.5 focus:outline-none"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <p className="text-xs text-center font-medium truncate text-foreground">{item.name}</p>
      )}
      <p className="text-[10px] text-center text-muted-foreground mt-0.5">
        {item.type === "file" ? fmtSize(item.size) : `${item.children ?? 0} items`}
      </p>
    </div>
  );
}
